// services/loincService.js
import axios from 'axios';
import {
    API_BASE_LOINC_URL,
  API_LOINC_ALL,
  API_LOINC_SEARCH,
  API_LOINC_STATS,
  API_LOINC_CATEGORIES,
  API_LOINC_VALIDATE,
  API_LOINC_BY_NUMBER,
  API_LOINC_BY_ID
} from '../../../helpers/urlHelper';

const API_BASE_URL = API_BASE_LOINC_URL;

// ========================================================================
// 🔍 BÚSQUEDA Y CONSULTAS
// ========================================================================

/**
 * Obtener todos los códigos LOINC con paginación opcional
 * @param {Object} params - Parámetros de consulta
 * @param {number} params.page - Número de página (opcional)
 * @param {number} params.limit - Límite por página (opcional)
 * @param {string} params.category - Filtrar por categoría (opcional)
 * @param {string} params.sortBy - Campo para ordenar (default: 'component')
 * @param {string} params.order - Orden: 'asc' o 'desc' (default: 'asc')
 * @returns {Promise<Object>} Lista de códigos LOINC
 */
export const getAllLoincCodes = async (params = {}) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_LOINC_ALL}`, { params });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo códigos LOINC:', error);
    throw error;
  }
};

/**
 * Buscar códigos LOINC
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.query - Texto de búsqueda
 * @param {string} params.category - Filtrar por categoría (opcional)
 * @param {number} params.page - Número de página (default: 1)
 * @param {number} params.limit - Límite por página (default: 20)
 * @param {boolean} params.exact - Búsqueda exacta por código LOINC (default: false)
 * @returns {Promise<Object>} Resultados de búsqueda con paginación
 */
export const searchLoincCodes = async (params) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_LOINC_SEARCH}`, { params });
    return response.data;
  } catch (error) {
    console.error('Error buscando códigos LOINC:', error);
    throw error;
  }
};

/**
 * Obtener código LOINC por número LOINC
 * @param {string} loincNum - Número LOINC (ej: "718-7")
 * @returns {Promise<Object>} Código LOINC encontrado
 */
export const getLoincCodeByNumber = async (loincNum) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_LOINC_BY_NUMBER}/${loincNum}`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo código LOINC por número:', error);
    throw error;
  }
};

/**
 * Obtener código LOINC por ID de MongoDB
 * @param {string} id - ID de MongoDB
 * @returns {Promise<Object>} Código LOINC encontrado
 */
export const getLoincCodeById = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_LOINC_BY_ID}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo código LOINC por ID:', error);
    throw error;
  }
};

// ========================================================================
// 📊 ESTADÍSTICAS Y METADATOS
// ========================================================================

/**
 * Obtener estadísticas de códigos LOINC
 * @returns {Promise<Object>} Estadísticas (total, por categoría, top clases)
 */
export const getLoincStats = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_LOINC_STATS}`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo estadísticas LOINC:', error);
    throw error;
  }
};

/**
 * Obtener todas las categorías disponibles
 * @returns {Promise<Object>} Array de categorías ordenadas
 */
export const getLoincCategories = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_LOINC_CATEGORIES}`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo categorías LOINC:', error);
    throw error;
  }
};

// ========================================================================
// ✅ VALIDACIÓN
// ========================================================================

/**
 * Validar si un código LOINC existe y está activo
 * @param {string} loincNum - Número LOINC a validar
 * @returns {Promise<Object>} Resultado de validación { valid, loincNum }
 */
export const validateLoincCode = async (loincNum) => {
  try {
    const response = await axios.post(`${API_BASE_URL}${API_LOINC_VALIDATE}`, { loincNum });
    return response.data;
  } catch (error) {
    console.error('Error validando código LOINC:', error);
    throw error;
  }
};

// ========================================================================
// 🔧 UTILIDADES
// ========================================================================

/**
 * Buscar códigos LOINC por categoría específica
 * @param {string} category - Categoría (ej: "Hematología", "Bioquímica")
 * @param {Object} additionalParams - Parámetros adicionales (page, limit, etc.)
 * @returns {Promise<Object>} Códigos LOINC de la categoría
 */
export const getLoincByCategory = async (category, additionalParams = {}) => {
  try {
    return await getAllLoincCodes({ category, ...additionalParams });
  } catch (error) {
    console.error('Error obteniendo códigos por categoría:', error);
    throw error;
  }
};

/**
 * Búsqueda rápida por texto (sin paginación)
 * @param {string} query - Texto de búsqueda
 * @param {number} limit - Límite de resultados (default: 10)
 * @returns {Promise<Object>} Primeros resultados encontrados
 */
export const quickSearchLoinc = async (query, limit = 10) => {
  try {
    return await searchLoincCodes({ query, limit, page: 1 });
  } catch (error) {
    console.error('Error en búsqueda rápida LOINC:', error);
    throw error;
  }
};

/**
 * Búsqueda exacta por código LOINC
 * @param {string} loincNum - Código LOINC exacto
 * @returns {Promise<Object>} Código LOINC encontrado
 */
export const exactSearchLoinc = async (loincNum) => {
  try {
    return await searchLoincCodes({ query: loincNum, exact: true });
  } catch (error) {
    console.error('Error en búsqueda exacta LOINC:', error);
    throw error;
  }
};

// ========================================================================
// 📦 EXPORTS
// ========================================================================

export default {
  getAllLoincCodes,
  searchLoincCodes,
  getLoincCodeByNumber,
  getLoincCodeById,
  getLoincStats,
  getLoincCategories,
  validateLoincCode,
  getLoincByCategory,
  quickSearchLoinc,
  exactSearchLoinc
};